import { mapBuild } from '../lib/hydrate.js';
import { downloadReviewDelivery } from '../lib/review-delivery.js';

function PipelineStepper({ stages }) {
  return (
    <div className="pipeline">
      {stages.map((s, i) => {
        const cls = 'pnode ' + s.status;
        const fill = s.status === 'done' ? '100%' : s.status === 'running' ? '55%' : '0';
        let glyph;
        if (s.status === 'done') glyph = <I.check size={22} />;
        else if (s.status === 'running') glyph = <div className="spinner"></div>;
        else if (s.status === 'failed') glyph = <I.x size={22} />;
        else if (s.status === 'review') glyph = <I.warn size={20} />;
        else glyph = <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{s.no}</span>;
        return (
          <div key={s.id} className={cls}>
            <span className="connector"><span style={{ width: fill }}></span></span>
            <div className="orb">{glyph}</div>
            <div className="plabel">{s.name}</div>
            <div className="pmeta"><span className="pmodel">{s.model}</span><span>{s.tokens} · {s.elapsed}</span></div>
            <div className="pstate" style={{
              color: s.status === 'done' ? 'var(--pass)' : s.status === 'running' ? 'var(--running)' :
                     s.status === 'failed' ? 'var(--fail)' : s.status === 'review' ? 'var(--cond)' : 'var(--faint)'
            }}>{s.status === 'review' ? 'Needs review' : s.status}</div>
          </div>
        );
      })}
    </div>
  );
}

function LiveLog({ job }) {
  // Engine-aware: codex builds stream their `codex exec --json` events through
  // this same Live-activity monitor (window.CODEX_LOG) instead of the swarm
  // liveLog. CODEX_LOG is the demo tap; the real tail of _codex/stream-N.jsonl
  // feeds here (supervisor / PY-05). Non-codex → the swarm liveLog, unchanged.
  const liveLog = (job && job.engine === 'codex' && window.CODEX_LOG) || window.MAV.liveLog;
  const [lines, setLines] = React.useState(liveLog.slice(0, 4));
  const ref = React.useRef(null);
  React.useEffect(() => {
    let i = 4;
    const t = setInterval(() => {
      if (i < liveLog.length) { setLines(liveLog.slice(0, i + 1)); i++; }
      else { i = 4; setLines(liveLog.slice(0, 4)); }
    }, 1400);
    return () => clearInterval(t);
  }, []);
  React.useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [lines]);
  return (
    <div className="logwrap" ref={ref}>
      {lines.map((l, i) => (
        <div key={i} className="log-line"><span className="ts">{l.t} </span><span className={l.c}>{l.m}</span></div>
      ))}
      <div className="log-line"><span className="cur">█</span></div>
    </div>
  );
}

function LiveEventLog({ events, status, error }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [events]);
  const lines = (events || []).slice(-90);
  const connecting = status === 'connecting' || status === 'reconnecting';
  return (
    <div className="logwrap" ref={ref} data-event-log-source="sse" data-sse-status={status || 'idle'}>
      {!lines.length && (
        <div className="log-line">
          <span className="ts">--:--:-- </span>
          <span className={error ? 'warn' : 'info'}>{error || (connecting ? 'Connecting to swarm event stream...' : 'Waiting for swarm events...')}</span>
        </div>
      )}
      {lines.map((ev, i) => (
        <div key={(ev.seq != null ? ev.seq : i) + '-' + (ev.type || 'event')} className="log-line">
          <span className="ts">{eventTimeLabel(ev.ts)} </span>
          <span className={eventLevel(ev)}>{formatEventMessage(ev)}</span>
        </div>
      ))}
      {error && lines.length > 0 && (
        <div className="log-line">
          <span className="ts">stream </span><span className="warn">{error}</span>
        </div>
      )}
      {status !== 'complete' && <div className="log-line"><span className="cur">█</span></div>}
    </div>
  );
}

// Relative MM:SS from a stage's first event timestamp — every stage's stream
// resets to 00:00 at its own start (matches the "00:0X" terminal style).
function relStageTime(t0, ts) {
  const d = ts ? new Date(ts).getTime() : NaN;
  if (!Number.isFinite(d) || !Number.isFinite(t0)) return '00:00';
  const sec = Math.max(0, Math.round((d - t0) / 1000));
  return String(Math.floor(sec / 60)).padStart(2, '0') + ':' + String(sec % 60).padStart(2, '0');
}

// One streamed line for a single stage. `log` events are the stage's own stdout
// (StdoutTee tags every print() with the running stage); stage_start/_end bookend.
function stageStreamMessage(ev) {
  if (!ev) return '';
  if (ev.type === 'log') return ev.msg || '';
  if (ev.type === 'stage_start') {
    const name = STAGE_LABEL[ev.stage] || ev.label || ev.stage;
    const model = shortModel(ev.model);
    return `Stage ${STAGE_NUM[ev.stage] || ''} · ${name}` + (model !== '—' ? ` — model ${model}` : '');
  }
  if (ev.type === 'stage_end') {
    const status = normalizeStageStatus(ev.status);
    const bits = [status === 'done' ? '✓ stage complete' : `stage ${status}`];
    if (ev.elapsed_s) bits.push(fmtElapsed(ev.elapsed_s));
    if (ev.usage && ev.usage.total_tokens) bits.push(fmtTokens(ev.usage.total_tokens) + ' tok');
    return bits.join(' · ');
  }
  return ev.msg || '';
}

// Per-stage live terminal: the streamed output of ONE stage, filtered from the
// SSE feed by ev.stage. Stage-agnostic by construction — EVERY stage streams its
// own narration through the same `log` events (Gemini ingest, Sonnet semantic,
// DeepSeek plan, Gemini execute, Perceptron+Gemini QA, DeepSeek approve).
function StageStreamLog({ events, stageId, status }) {
  const ref = React.useRef(null);
  const lines = (events || []).filter(
    (ev) => ev && ev.stage === stageId &&
      (ev.type === 'log' || ev.type === 'stage_start' || ev.type === 'stage_end'),
  );
  const t0 = lines.length ? new Date(lines[0].ts).getTime() : NaN;
  React.useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [lines.length]);
  const connecting = status === 'connecting' || status === 'reconnecting';
  return (
    <div className="logwrap" ref={ref} data-stage-log={stageId || 'none'} data-stage-log-lines={lines.length}>
      {!lines.length && (
        <div className="log-line">
          <span className="ts">00:00 </span>
          <span className="info">{connecting ? 'Connecting to swarm event stream…' : 'Waiting for this stage to stream…'}</span>
        </div>
      )}
      {lines.map((ev, i) => (
        <div key={ev.seq != null ? ev.seq : i} className="log-line">
          <span className="ts">{relStageTime(t0, ev.ts)} </span>
          <span className={eventLevel(ev)}>{stageStreamMessage(ev)}</span>
        </div>
      ))}
      {status !== 'complete' && <div className="log-line"><span className="cur">█</span></div>}
    </div>
  );
}

// CodexStreamLog (PY-35) — a live CODEX build has no swarm stages; render its own
// `log` events from the events.ndjson SSE (not the stage-scoped ActiveStageLog).
function CodexStreamLog({ events, status }) {
  const ref = React.useRef(null);
  const lines = (events || []).filter((ev) => ev && ev.type === 'log');
  const t0 = lines.length ? new Date(lines[0].ts).getTime() : NaN;
  React.useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [lines.length]);
  const connecting = status === 'connecting' || status === 'reconnecting';
  return (
    <div className="logwrap" ref={ref} data-codex-log-lines={lines.length}>
      {!lines.length && (
        <div className="log-line">
          <span className="ts">00:00 </span>
          <span className="info">{connecting ? 'Connecting to the codex stream…' : 'Waiting for codex to stream…'}</span>
        </div>
      )}
      {lines.map((ev, i) => (
        <div key={ev.seq != null ? ev.seq : i} className="log-line">
          <span className="ts">{relStageTime(t0, ev.ts)} </span>
          <span className={eventLevel(ev)}>{ev.msg || ''}</span>
        </div>
      ))}
      {status !== 'complete' && <div className="log-line"><span className="cur">█</span></div>}
    </div>
  );
}

// Active-stage log card body: defaults to THIS stage's own stream (the screenshot
// view); a toggle flips to codex's combined all-events feed. Additive — leaves
// LiveEventLog untouched so item-8's full event log stays one click away.
function ActiveStageLog({ events, status, error, stageId }) {
  const [mode, setMode] = React.useState('stage');
  return (
    <React.Fragment>
      <div className="tabs" style={{ marginBottom: 8 }} data-active-stage-log={stageId || 'none'} data-log-mode={mode}>
        <span className={'tab' + (mode === 'stage' ? ' active' : '')} data-logmode="stage" onClick={() => setMode('stage')}>This stage</span>
        <span className={'tab' + (mode === 'all' ? ' active' : '')} data-logmode="all" onClick={() => setMode('all')}>All events</span>
      </div>
      {mode === 'stage'
        ? <StageStreamLog events={events} stageId={stageId} status={status} />
        : <LiveEventLog events={events} status={status} error={error} />}
    </React.Fragment>
  );
}

// Per-stage expandable log accordion under the horizontal stepper: each stage is
// a full-width row that expands to its OWN scrollable mini-terminal (StageStreamLog
// scoped to that stage, max-height 230px, overflow-y auto).
// Multiple rows can be open at once; completed stages keep their full history.
// The currently running stage is auto-expanded whenever its ID becomes known.
function StageLogAccordion({ stages, events }) {
  const [open, setOpen] = React.useState(() => new Set());
  const runningId = ((stages || []).find((s) => s.status === 'running') || {}).id || null;
  // Auto-expand the running stage whenever it becomes known (and again when its
  // canonical ID is established after the first stage_start event normalizes it).
  React.useEffect(() => {
    if (!runningId) return;
    setOpen((prev) => {
      if (prev.has(runningId)) return prev;
      const next = new Set(prev);
      next.add(runningId);
      return next;
    });
  }, [runningId]);
  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const counts = {};
  for (const ev of (events || [])) {
    if (ev && ev.stage && (ev.type === 'log' || ev.type === 'stage_start' || ev.type === 'stage_end')) {
      counts[ev.stage] = (counts[ev.stage] || 0) + 1;
    }
  }
  const stateColor = (st) => st === 'done' ? 'var(--pass)' : st === 'running' ? 'var(--running)'
    : st === 'failed' ? 'var(--fail)' : st === 'review' ? 'var(--cond)' : 'var(--faint)';
  return (
    <div className="stage-logs" data-stage-logs style={{ marginTop: 14, paddingTop: 8 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Stage logs</div>
      {(stages || []).map((s) => {
        const n = counts[s.id] || 0;
        const isOpen = open.has(s.id);
        const streamStatus = s.status === 'running' ? 'running' : 'complete';
        return (
          <div key={s.id} className="stage-log-row" data-stage-log-row={s.id} data-open={isOpen ? '1' : '0'}>
            <button
              type="button"
              onClick={() => toggle(s.id)}
              data-stage-log-toggle={s.id}
              aria-expanded={isOpen}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'transparent', borderWidth: 0, borderTop: '1px solid var(--line, rgba(0,0,0,0.06))', cursor: 'pointer', padding: '7px 2px', font: 'inherit', color: 'inherit' }}
            >
              <span className="mono" style={{ fontSize: 11, color: 'var(--faint)', minWidth: 34 }}>{isOpen ? '▾' : '▸'} {s.no}</span>
              <span style={{ fontWeight: 700, fontSize: 12.5 }}>{s.name}</span>
              <span className="chip chip-mono" style={{ fontSize: 10 }}>{s.model || '—'}</span>
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--faint)', fontFamily: 'var(--mono)' }}>{n ? n + ' lines' : 'no log'}</span>
              <span className="pstate" style={{ marginLeft: 6, color: stateColor(s.status) }}>{s.status === 'review' ? 'review' : s.status}</span>
            </button>
            {isOpen && (
              <div style={{ marginTop: 4, marginBottom: 6 }}>
                <StageStreamLog events={events} stageId={s.id} status={streamStatus} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Mock artifacts kept as a fallback for the demo flow (no _id → no live fetch).
// When a live build is opened, fetched detail replaces these.
const MOCK_ARTIFACTS = [
  { ico: 'doc',    n: 'brief.md',                          d: 'Ingestion · 38K tok',  stage: 0, dir: '01_ingestion/',  files: [
    { n: 'brief.md', d: 'Structured creative brief', primary: true },
    { n: 'inventory.json', d: 'Source asset inventory' },
    { n: 'raw-response.json', d: 'Raw model output', raw: true },
  ] },
  { ico: 'json',   n: 'semantic-frames.json',              d: '6 element crops',      stage: 1, dir: '01_6_semantic/', files: [
    { n: 'semantic-frames.json', d: 'Frame map', primary: true },
    { n: 'element-crops.json', d: 'Crop coordinates' },
    { n: 'vision-crop-plan.json', d: 'Vision crop plan' },
    { n: 'raw-response.json', d: 'Raw model output', raw: true },
  ] },
  { ico: 'doc',    n: 'code-plan.md',                      d: 'Planning · build spec', stage: 2, dir: '02_planning/',  files: [
    { n: 'code-plan.md', d: 'Build spec', primary: true },
    { n: 'detected-client.txt', d: 'Client detection' },
    { n: 'raw-response.json', d: 'Raw model output', raw: true },
  ] },
  { ico: 'code',   n: 'index.html + images/',              d: 'Execution output',     stage: 3, dir: '03_execution/',  files: [
    { n: 'index.html', d: 'Rendered email', primary: true },
    { n: 'images/', d: 'Copied build assets' },
    { n: 'raw-response.json', d: 'Raw model output', raw: true },
  ] },
  { ico: 'review', n: 'qa-report.md',                      d: 'Verdict + findings',   stage: 4, dir: '04_qa/',         files: [
    { n: 'qa-report.md', d: 'Verdict + findings', primary: true },
    { n: 'checklist-report.md', d: 'Checklist results' },
    { n: 'image-ref-audit.json', d: 'Broken-ref audit' },
    { n: 'vision-report.md', d: 'Visual-diff report' },
  ] },
  { ico: 'zip',    n: '15012935_SYF_FLR_0176.zip',         d: 'Delivery package',     stage: 5, dir: '05_approval/',   files: [
    { n: '15012935_SYF_FLR_0176.zip', d: 'Delivery package', primary: true },
    { n: 'approval.md', d: 'Approval verdict' },
    { n: 'changelog-entry.md', d: 'Changelog entry' },
  ] },
];

// Map a stage_key ('01_ingestion', '01_6_semantic', …) to its dashboard slot 0–5.
const STAGE_SLOT = { '01_ingestion': 0, '01_6_semantic': 1, '02_planning': 2, '03_execution': 3, '04_qa': 4, '05_approval': 5 };
const STAGE_NUM = { '01_ingestion': '01', '01_6_semantic': '1.6', '02_planning': '02', '03_execution': '03', '04_qa': '04', '05_approval': '05' };
const STAGE_LABEL = { '01_ingestion': 'Ingestion', '01_6_semantic': 'Semantic', '02_planning': 'Planning', '03_execution': 'Execution', '04_qa': 'QA', '05_approval': 'Approval' };
// Mirrors swarm/lib/events.py _artifact_kind enum: doc/json/code/report/archive/image/assets.
const KIND_TO_ICO = { doc: 'doc', json: 'json', code: 'code', report: 'review', archive: 'zip', image: 'image', assets: 'assets' };
function ICO(name) {
  if (name === 'doc') return I.doc;
  if (name === 'json') return I.json;
  if (name === 'code') return I.code;
  if (name === 'review') return I.review;
  if (name === 'zip') return I.zip;
  if (name === 'image') return I.image;
  if (name === 'assets') return I.layers;
  return I.doc;
}

// "anthropic/claude-4.6-sonnet-20260217" → "claude-4.6-sonnet"
function shortModel(m) {
  if (!m) return '—';
  const tail = String(m).split('/').pop();
  return tail.replace(/-\d{8}$/, '');
}
function fmtTokens(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '—';
  return v >= 1000 ? (v / 1000).toFixed(1) + 'K' : String(v);
}
function fmtElapsed(s) {
  const v = Number(s);
  if (!Number.isFinite(v) || v <= 0) return '—';
  if (v < 60) return Math.round(v) + 's';
  const m = Math.floor(v / 60), r = Math.round(v % 60);
  return m + 'm' + (r ? r + 's' : '');
}

function normalizeStageStatus(status) {
  const s = String(status || '').toLowerCase();
  if (['ok', 'done', 'complete', 'completed', 'pass', 'passed'].includes(s)) return 'done';
  if (['fail', 'failed', 'error', 'errored'].includes(s)) return 'failed';
  if (['review', 'needs_review', 'conditional'].includes(s)) return 'review';
  if (s === 'running') return 'running';
  return 'queued';
}

function eventTimeLabel(ts) {
  const d = ts ? new Date(ts) : null;
  if (!d || Number.isNaN(d.getTime())) return '--:--:--';
  return d.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function eventLevel(ev) {
  const s = String((ev && ev.status) || '').toLowerCase();
  const type = ev && ev.type;
  const msg = String((ev && ev.msg) || '');
  const level = String((ev && ev.level) || '').toLowerCase();
  if (level === 'warn' || level === 'warning' || level === 'error') return 'warn';
  if (['fail', 'failed', 'error', 'errored'].includes(s) || /error|failed|traceback/i.test(msg)) return 'warn';
  if (type === 'stage_end' || type === 'run_end' || type === 'artifact') return 'ok';
  if (type === 'stage_start' || type === 'run_start') return 'info';
  return 'info';
}

function formatEventMessage(ev) {
  if (!ev) return '';
  const stageName = ev.label || STAGE_LABEL[ev.stage] || ev.stage || 'stage';
  if (ev.type === 'run_start') return `Run started (${ev.source || 'swarm'})`;
  if (ev.type === 'stage_start') return `Stage ${stageName} started`;
  if (ev.type === 'stage_end') {
    const status = normalizeStageStatus(ev.status);
    const suffix = status === 'done' ? 'completed' : status;
    const bits = [`Stage ${stageName} ${suffix}`];
    if (ev.elapsed_s) bits.push(fmtElapsed(ev.elapsed_s));
    if (ev.usage && ev.usage.total_tokens) bits.push(fmtTokens(ev.usage.total_tokens));
    return bits.join(' · ');
  }
  if (ev.type === 'artifact') return `Artifact ready: ${ev.rel_path || ev.name || 'output'}`;
  if (ev.type === 'run_end') return `Run ended: ${String(ev.verdict || 'complete').toUpperCase()}`;
  if (ev.type === 'log') return ev.msg || '';
  return ev.msg || ev.type || 'event';
}

function stageFromKey(stageKey, ev) {
  return {
    id: stageKey,
    no: STAGE_NUM[stageKey] || '—',
    name: (ev && ev.label) || STAGE_LABEL[stageKey] || stageKey,
    model: shortModel(ev && ev.model),
    status: 'queued',
    tokens: '—',
    elapsed: '—',
  };
}

function applyEventToStages(currentStages, ev) {
  if (!ev || !ev.stage || !['stage_start', 'stage_end'].includes(ev.type)) return currentStages || null;
  const base = (currentStages && currentStages.length)
    ? currentStages.map((s) => ({ ...s }))
    : Object.keys(STAGE_SLOT).map((key) => stageFromKey(key));
  let idx = base.findIndex((s) => s.id === ev.stage);
  // Hydrate/NewBuild stubs use synthetic ids before the real stage_key arrives.
  // Claim the canonical slot instead of appending a duplicate stage when the SSE
  // replay starts before /api/data/builds/:id has projected.
  if (idx < 0) {
    const slot = STAGE_SLOT[ev.stage];
    if (slot != null && base[slot]) idx = slot;
  }
  if (idx < 0) {
    base.push(stageFromKey(ev.stage, ev));
    base.sort((a, b) => parseFloat(a.no) - parseFloat(b.no));
    idx = base.findIndex((s) => s.id === ev.stage);
  }
  const target = base[idx];
  target.id = ev.stage;
  target.no = STAGE_NUM[ev.stage] || target.no;
  // The canonical slots are shared across engines, but their work is not. Prefer
  // the engine-emitted label (for example Sabre's "Design analysis") and retain
  // the classic label only as the compatibility fallback.
  target.name = ev.label || STAGE_LABEL[ev.stage] || target.name;
  const model = shortModel(ev.model);
  if (model !== '—') target.model = model;
  if (ev.type === 'stage_start') target.status = 'running';
  if (ev.type === 'stage_end') {
    target.status = normalizeStageStatus(ev.status);
    if (ev.usage && ev.usage.total_tokens) target.tokens = fmtTokens(ev.usage.total_tokens);
    if (ev.elapsed_s) target.elapsed = fmtElapsed(ev.elapsed_s);
  }
  return base;
}

function eventBuildPatch(ev) {
  if (!ev || ev.type !== 'run_end') return null;
  // A GENUINE pipeline failure carries an explicit failed status or an error on
  // run_end (the swarm sets these on a crash mid-run). A normal finish leaves
  // them null and reports only the QA verdict.
  const runStatus = String(ev.status || '').toLowerCase();
  if (ev.error || ['failed', 'error', 'errored'].includes(runStatus)) {
    return { status: 'failed', verdict: String(ev.verdict || '').toUpperCase() || null };
  }
  // QA verdict on a COMPLETED run. Mirror db/projector.js manifestStatus()
  // EXACTLY: approval reached → PASS is 'complete', anything else awaits review.
  // verdict='FAIL' is a QA score on a finished build, NOT a pipeline break — it
  // must NOT map to status='failed' (that's "the pipeline crashed; restart it").
  const verdict = String(ev.verdict || '').toUpperCase();
  if (verdict === 'PASS') return { status: 'complete', verdict: 'PASS' };
  if (verdict === 'FAIL' || verdict === 'CONDITIONAL') return { status: 'needs_review', verdict };
  return { status: 'complete', verdict: verdict || null };
}

function eventArtifactRow(ev) {
  if (!ev || ev.type !== 'artifact' || !ev.rel_path) return null;
  const dir = ev.rel_path.split('/').slice(0, -1).join('/');
  return {
    stage_key: ev.stage || dir,
    name: ev.name || ev.rel_path.split('/').pop(),
    rel_path: ev.rel_path,
    kind: ev.kind || 'doc',
    size_bytes: ev.size || ev.size_bytes || 0,
  };
}

function mergeArtifactRows(primary, secondary) {
  const byKey = new Map();
  for (const a of [...(primary || []), ...(secondary || [])]) {
    if (!a) continue;
    const key = a.rel_path || `${a.stage_key || ''}:${a.name || ''}`;
    byKey.set(key, a);
  }
  return [...byKey.values()];
}

function patchMavJob(jobId, patch) {
  if (!jobId || !patch || !(window.MAV && Array.isArray(window.MAV.jobs))) return;
  const idx = window.MAV.jobs.findIndex((j) => j.id === jobId);
  if (idx < 0) return;
  window.MAV.jobs[idx] = { ...window.MAV.jobs[idx], ...patch };
}

function BuildClientPicker({ jobId, value, onAssigned }) {
  const clients = ((window.MAV && window.MAV.clients) || [])
    .filter((client) => client && client.slug && client.slug !== 'unknown')
    .sort((a, b) => String(a.name || a.slug).localeCompare(String(b.name || b.slug)));
  const current = value && value !== '__none__' ? value : 'unknown';
  const hasCurrent = current === 'unknown' || clients.some((client) => client.slug === current);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  const choose = async (ev) => {
    const next = ev.target.value === 'unknown' ? null : ev.target.value;
    if ((next || 'unknown') === current) return;
    setSaving(true);
    setError('');
    try {
      const result = await window.GooseAPI.assignBuildClient(jobId, next);
      const slug = (result && result.build && result.build.client_slug) || 'unknown';
      onAssigned(slug, result && result.build);
    } catch (e) {
      setError((e && e.message) || 'Client assignment failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="client-assign" data-client-assign={current}>
      <select
        className="input"
        value={current}
        onChange={choose}
        disabled={saving || !(window.GooseAPI && window.GooseAPI.assignBuildClient)}
        title="Assign this build to a KB client"
        aria-label="Assign build client"
      >
        <option value="unknown">Unassigned</option>
        {!hasCurrent && <option value={current}>{current}</option>}
        {clients.map((client) => (
          <option key={client.slug} value={client.slug}>{client.name || client.slug}</option>
        ))}
      </select>
      {saving && <span className="mono muted">saving</span>}
      {error && <span className="client-assign-error" role="alert">{error}</span>}
    </span>
  );
}

// Convert /api/data/builds/:id stages[] into the {id,no,name,model,status,tokens,elapsed} shape
// the existing PipelineStepper renders. Sort by parseFloat(no) so '01_6_semantic' (no='1.6')
// sits between '01_ingestion' (no='01') and '02_planning' (no='02').
function mapLiveStages(liveStages) {
  const sorted = [...liveStages].sort((a, b) => parseFloat(a.no) - parseFloat(b.no));
  return sorted.map((s) => ({
    id: s.stage_key,
    no: s.no,
    name: s.name,
    model: shortModel(s.model),
    status: s.status || 'queued',
    tokens: fmtTokens(s.tokens),
    elapsed: fmtElapsed(s.elapsed_s),
  }));
}

// Figma builds: the REST-direct "Figma Ingest" stage does the work of BOTH ingest
// AND the Semantic Parser with NO model call — s01_figma marks 01_ingestion +
// 01_6_semantic done together and records no model, and run_swarm frames it as ONE
// stage that "replaces ingest + semantic". So collapse the two into a single
// "Figma Ingest" stage. Without this, figma shows a phantom Semantic stage falsely
// labeled with the PDF pipeline's Sonnet model (the stageDefs default), implying a
// Sonnet agent ran when none did. Idempotent + applies across every rail source
// (live SSE / DB / synth), so the count + the "N of M" label stay honest (5, not 6).
function collapseFigmaIngest(stages, source) {
  if (source !== 'figma' || !Array.isArray(stages) || !stages.length) return stages;
  const isSem = (s) => s.id === 'semantic' || s.id === '01_6_semantic';
  const isIng = (s) => s.id === 'ingest' || s.id === '01_ingestion';
  return stages.filter((s) => !isSem(s)).map((s) =>
    isIng(s) ? { ...s, name: 'Figma Ingest', model: 'REST-direct (no model)' } : s);
}

// On a FAILED run the rail can carry FABRICATED downstream states: synthStages
// assumes a fixed failure slot (failed → stage 3 = Execution) and `run_end` carries
// no per-stage update (applyEventToStages ignores it), so the synth seed's bogus
// done/failed stages survive and the real-but-incomplete stage stays stuck 'running'
// (e.g. a build that died in Semantic showed Semantic=running, Planning=done,
// Execution=failed — none of it true). In an ordered pipeline the FIRST non-'done'
// stage IS where it died → mark that one 'failed', everything before 'done',
// everything after 'queued'. Makes the rail/banner/"resume from"/header all agree.
function normalizeFailedRail(stages) {
  if (!Array.isArray(stages) || !stages.length) return stages;
  let fp = stages.findIndex((s) => s.status !== 'done');
  if (fp < 0) fp = stages.length - 1;   // all 'done' yet the run failed → blame the last stage
  return stages.map((s, i) => ({ ...s, status: i < fp ? 'done' : i === fp ? 'failed' : 'queued' }));
}

// Convert flat artifacts[] into the existing nested {ico,n,d,stage,dir,files[]} shape.
// One group per stage (slot 0–5), with the first emitted artifact treated as primary.
function mapLiveArtifacts(liveArtifacts) {
  const bySlot = new Map();
  for (const a of liveArtifacts) {
    const dir = (a.rel_path || '').split('/').slice(0, -1).join('/') + '/';
    const stageKey = dir.replace(/\/$/, '');
    const slot = STAGE_SLOT[stageKey];
    if (slot == null) continue;
    if (!bySlot.has(slot)) bySlot.set(slot, { stage: slot, dir, primary: null, files: [] });
    const g = bySlot.get(slot);
    const fileEntry = { n: a.name, d: a.kind + (a.size_bytes ? ' · ' + (a.size_bytes < 1024 ? a.size_bytes + 'B' : Math.round(a.size_bytes / 1024) + 'KB') : '') };
    if (!g.primary) { g.primary = a; fileEntry.primary = true; }
    g.files.push(fileEntry);
  }
  return [...bySlot.values()]
    .sort((a, b) => a.stage - b.stage)
    .map((g) => ({
      ico: KIND_TO_ICO[g.primary.kind] || 'doc',
      n: g.primary.name,
      d: g.primary.kind + (g.files.length > 1 ? ' · ' + g.files.length + ' files' : ''),
      stage: g.stage,
      dir: g.dir,
      files: g.files,
    }));
}

// Real rendered-email preview: <img> sourced from the artifact endpoint at
// `/api/events/<job>/artifact?path=04_qa/{desktop-600px,mobile-375px}.png`.
// On 404 we POST /api/builds/<job>/render to fire Playwright server-side, then
// re-load the image (only the first 404 triggers a render — guard against the
// `render failed` 500 path from looping).
function RealEmailPreview({ jobId, device }) {
  const rel = device === 'mobile' ? '04_qa/mobile-375px.png' : '04_qa/desktop-600px.png';
  const baseUrl = window.GooseAPI && window.GooseAPI.artifactUrl ? window.GooseAPI.artifactUrl(jobId, rel) : '';
  const [cacheBust, setCacheBust] = React.useState(0);
  const [rendering, setRendering] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Track attempts (not a boolean) so a partial first render or "swarm not
  // finished yet" failure doesn't dead-end the preview forever.
  const attemptsRef = React.useRef(0);
  React.useEffect(() => { attemptsRef.current = 0; setError(null); setCacheBust(0); setRendering(false); }, [jobId]);
  const runRender = async () => {
    if (attemptsRef.current >= 2) { setError('render failed after 2 attempts'); return; }
    attemptsRef.current += 1;
    setRendering(true);
    try {
      if (window.GooseAPI && window.GooseAPI.renderBuild) await window.GooseAPI.renderBuild(jobId);
      setCacheBust((n) => n + 1);
      setError(null);
    } catch (e) {
      setError((e && e.message) || 'Could not render this build.');
    } finally {
      setRendering(false);
    }
  };
  const src = baseUrl + (cacheBust ? '&_=' + cacheBust : '');
  return (
    <div className="preview-frame" style={{ position: 'relative', minHeight: 320 }} data-preview-mode="live">
      {rendering && <div className="muted" style={{ position: 'absolute', top: 16, left: 0, right: 0, textAlign: 'center', fontSize: 12, zIndex: 1 }}>Rendering preview…</div>}
      {error
        ? <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }} role="alert">
            Couldn't render this build's preview ({error}). The artifact endpoint may not have <span className="mono">{rel}</span> yet.{' '}
            <button type="button" className="btn btn-quiet" style={{ marginTop: 10 }} onClick={() => { setError(null); runRender(); }}>Retry</button>
          </div>
        : <img alt="rendered email" src={src} onError={runRender} style={{ display: 'block', margin: '0 auto', maxWidth: device === 'mobile' ? 375 : 600, width: '100%', height: 'auto', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }} />
      }
    </div>
  );
}

// Modal: fetch an artifact's content and render by type.
//   .md / .txt → <pre>, .json → pretty-printed, .png/.jpg/.gif → <img>,
//   .html → <pre> (source, never rendered), .zip → download link.
function ArtifactViewer({ jobId, relPath, onClose }) {
  const [state, setState] = React.useState({ loading: true, text: null, contentType: '', error: null });
  React.useEffect(() => {
    let alive = true;
    setState({ loading: true, text: null, contentType: '', error: null });
    const lower = (relPath || '').toLowerCase();
    const isBinary = /\.(png|jpe?g|gif|zip)$/.test(lower);
    if (isBinary) { setState({ loading: false }); return; }
    (async () => {
      try {
        const r = await window.GooseAPI.fetchArtifactText(jobId, relPath);
        if (!alive) return;
        let body = r.text;
        if (lower.endsWith('.json')) {
          try { body = JSON.stringify(JSON.parse(r.text), null, 2); } catch { /* leave raw */ }
        }
        setState({ loading: false, text: body, contentType: r.contentType, error: null });
      } catch (e) {
        if (alive) setState({ loading: false, text: null, contentType: '', error: (e && e.message) || 'fetch failed' });
      }
    })();
    return () => { alive = false; };
  }, [jobId, relPath]);
  const lower = (relPath || '').toLowerCase();
  const fileUrl = window.GooseAPI && window.GooseAPI.artifactUrl ? window.GooseAPI.artifactUrl(jobId, relPath) : '#';
  return ReactDOM.createPortal(
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900, width: '92vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }} data-artifact-viewer>
        <div className="fb-head" style={{ flex: '0 0 auto' }}>
          <div>
            <div className="row gap-8" style={{ marginBottom: 6 }}>
              <span className="chip mono" style={{ fontSize: 11 }}>{relPath}</span>
            </div>
            <h2 className="fb-title" style={{ fontSize: 15 }}>Artifact</h2>
          </div>
          <button className="icon-act" onClick={onClose} title="Close"><I.x size={16} /></button>
        </div>
        <div className="fb-body" style={{ flex: 1, overflow: 'auto', padding: '14px 20px' }}>
          {state.loading && <p className="muted" style={{ fontSize: 12.5 }}>Loading…</p>}
          {state.error && <p style={{ fontSize: 13, color: 'var(--fail)' }} role="alert">Couldn't load: {state.error}</p>}
          {/\.(png|jpe?g|gif)$/.test(lower) && <img alt={relPath} src={fileUrl} style={{ maxWidth: '100%', display: 'block', margin: '0 auto', borderRadius: 4 }} />}
          {lower.endsWith('.zip') && <p style={{ fontSize: 13 }}>Binary archive — <a href={fileUrl} download style={{ color: 'var(--brand-orange)', fontWeight: 600 }}>download</a>.</p>}
          {state.text != null && (
            <pre style={{ background: 'var(--paper-2)', padding: 14, borderRadius: 6, fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '70vh', overflow: 'auto', margin: 0 }}>{state.text}</pre>
          )}
        </div>
        <div className="fb-foot" style={{ flex: '0 0 auto' }}>
          <span className="muted mono" style={{ fontSize: 11 }}>{state.contentType}</span>
          <div className="row gap-10">
            <a className="btn btn-ghost" href={fileUrl} target="_blank" rel="noopener noreferrer">Open raw <I.arrow size={13} /></a>
            <button className="btn btn-quiet" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function JobDetail({ job, go, back, openReview, openJob }) {
  const [assignedClient, setAssignedClient] = React.useState(job.client || 'unknown');
  const [tick, setTick] = React.useState(0);
  const [abStatus, setAbStatus] = React.useState(null);
  const [abBusy, setAbBusy] = React.useState(false);
  const [abIntake, setAbIntake] = React.useState(false);  // intake-method chooser open?
  const [device, setDevice] = React.useState('desktop');   // desktop 600px | mobile 375px
  const [openArt, setOpenArt] = React.useState(null);      // expanded artifact stage
  const [viewerArtifact, setViewerArtifact] = React.useState(null);  // {relPath} | null
  const [deleting, setDeleting] = React.useState(false);   // delete-build confirm modal
  const [limitDismissed, setLimitDismissed] = React.useState(false);  // codex usage-limit banner dismissed?
  // Admin gate for destructive delete. Read the RAW role enum off the live /me
  // (MAV.currentUser is the live object; MAV.me() is the stale mock closure and
  // returns undefined live). Accept admin AND product_manager (the lead role the
  // dev account uses post-rename) — matching the server DELETE_ROLES gate.
  const _cu = window.MAV.currentUser;
  const _role = (_cu && typeof _cu === 'object' && _cu.role) || '';
  const isAdmin = /admin|product[_ ]manager/i.test(_role);
  // RBAC-07: gate privileged actions via the shared role gate when a live viewer is
  // present; else keep today's behavior (the server enforces regardless). One source
  // of truth = ROLE_MATRIX (delete=admin/PM, cancel=builder/lead/PM/admin, reassign=lead/PM/admin).
  const _rbacV = (window.RBAC && window.RBAC.viewer && window.RBAC.viewer()) || null;
  const _can = (action, fallback) => (_rbacV ? window.RBAC.requireRole(action) : fallback);
  const canDelete = _can('delete', isAdmin);
  const canCancel = _can('cancel', true);
  const canRetry = _can('start', false);
  const canReassign = _can('reassign', true);
  const [reporting, setReporting] = React.useState(false);  // post-delivery report modal
  const [cancelling, setCancelling] = React.useState(false);  // operator "kill build" in flight
  const [resuming, setResuming] = React.useState(false);
  const [resumeError, setResumeError] = React.useState(null);
  const [recoveryNotice, setRecoveryNotice] = React.useState(null);
  const [retryReceipts, setRetryReceipts] = React.useState([]);
  const [attemptHistory, setAttemptHistory] = React.useState(null);
  const [attemptError, setAttemptError] = React.useState(null);
  const retryView = React.useRef(0);
  const refreshRetryReceipts = () => {
    try { setRetryReceipts(window.GooseAPI?.savedRetryRequests?.(job.id) || []); }
    catch (e) { setResumeError(e.message); }
  };
  React.useEffect(() => {
    retryView.current++;
    refreshRetryReceipts();
    const context = window.MAV.__contextKey, epoch = window.MAV.__contextEpoch, actor = window.MAV.currentUser?.id;
    let alive = true;
    if (job._live || job._id || job._stub) window.GooseAPI?.buildAttempts?.(job.id)
      .then(data => { if (alive) setAttemptHistory(data); })
      .catch(e => { if (alive) setAttemptError(e.message); });
    const changed = () => {
      if (window.MAV.__contextKey === context && window.MAV.__contextEpoch === epoch && window.MAV.currentUser?.id === actor) {
        refreshRetryReceipts(); return;
      }
      retryView.current++; setResuming(false); setRetryReceipts([]); setAttemptHistory(null); setResumeError(null); setRecoveryNotice(null); setAttemptError(null);
    };
    window.addEventListener('goose-rbac-change', changed);
    return () => { alive = false; retryView.current++; window.removeEventListener('goose-rbac-change', changed); };
  }, [job.id]);
  const [dlErr, setDlErr] = React.useState(null);  // delivery-zip download error
  React.useEffect(() => {
    setAssignedClient(job.client || 'unknown');
  }, [job && job.id, job && job.client]);
  const clientSlug = assignedClient || 'unknown';
  const c = window.MAV.clientBySlug(clientSlug);
  const displayJob = { ...job, client: clientSlug };
  const handleClientAssigned = (slug, buildRow) => {
    const next = slug || 'unknown';
    setAssignedClient(next);
    patchMavJob(job.id, { client: next });
    if (buildRow) {
      setDetail((prev) => prev
        ? { ...prev, build: { ...(prev.build || {}), client_slug: buildRow.client_slug || null } }
        : prev);
    }
  };
  // Download the delivery package. The backend resolves + streams the zip (it
  // isn't always an artifact event), so we fetch it as a blob and save it —
  // with a clean message when the build produced no package.
  const downloadDelivery = async () => {
    setDlErr(null);
    try { await downloadReviewDelivery(job.id); }
    catch (error) { setDlErr(error.message); }
  };
  // Live detail: stages + artifacts from /api/data/builds/:id, only when the job
  // carries a real DB uuid (set by hydrate.js for live builds). Mock jobs skip the
  // fetch entirely; loading state falls back to the existing job.stages.
  // detail.build carries the up-to-date status/verdict; without this the stub's
  // initial status (e.g. 'queued') sticks even after the swarm finishes.
  const [detail, setDetail] = React.useState(null);        // {build, stages, artifacts} | null
  const [eventState, setEventState] = React.useState({ status: 'idle', events: [], error: null });
  const [eventStages, setEventStages] = React.useState(null);
  const [eventArtifacts, setEventArtifacts] = React.useState([]);
  const [eventBuild, setEventBuild] = React.useState(null);
  const eventSeqRef = React.useRef(new Set());
  React.useEffect(() => {
    setDetail(null);
    // Try the DB uuid first; for fresh stub jobs without a uuid yet (just-started),
    // fall back to looking up by job_id slug via /api/data/builds + filtering.
    const fetchBy = job && (job._id || job.id);
    if (!fetchBy || !(window.GooseAPI && window.GooseAPI.build && window.GooseAPI.builds)) return;
    let alive = true;
    let timer = null;
    const tick = async () => {
      try {
        // We poll until we see the build's status reach a terminal state.
        // If job._id is missing (stub from a just-clicked Run Build), look it
        // up by job_id slug in the builds list — the projector will surface
        // the row on its next 15s tick.
        let uuid = job._id;
        if (!uuid) {
          try {
            const list = await window.GooseAPI.builds();
            const row = (list && list.builds || []).find((b) => b.job_id === job.id);
            if (row) uuid = row.id;
          } catch (_) {}
        }
        if (!uuid) return;     // not projected yet — retry on the next tick
        const d = await window.GooseAPI.build(uuid);
        if (!alive || !d || !Array.isArray(d.stages)) return;
        setDetail({ build: d.build || null, stages: d.stages, artifacts: d.artifacts || [] });
        if (d.build) setAssignedClient(d.build.client_slug || 'unknown');
        // Reflect live status/verdict back into MAV.jobs so the dashboard table
        // sees the truth too (otherwise it stays at the stub's 'queued').
        if (d.build && window.MAV && Array.isArray(window.MAV.jobs)) {
          const idx = window.MAV.jobs.findIndex((j) => j.id === job.id);
          if (idx >= 0) {
            const cur = window.MAV.jobs[idx];
            window.MAV.jobs[idx] = {
              ...cur,
              status: d.build.status || cur.status,
              verdict: d.build.verdict || cur.verdict,
              client: d.build.client_slug || 'unknown',
              _id: d.build.id || cur._id,
              _stub: false,
            };
          }
        }
      } catch (_) { /* keep retrying */ }
    };
    tick();   // immediate fetch
    // While the build isn't terminal, poll every 5s. Terminal = the latest
    // detail.build.status is one of complete / needs_review / failed.
    const TERMINAL = new Set(['complete', 'needs_review', 'failed']);
    const POLL_MS = 5000;
    const startPolling = () => {
      timer = setInterval(async () => {
        const cur = await new Promise((r) => { setDetail((d) => { r(d); return d; }); });
        if (cur && cur.build && TERMINAL.has(cur.build.status)) { clearInterval(timer); timer = null; return; }
        tick();
      }, POLL_MS);
    };
    startPolling();
    return () => { alive = false; if (timer) clearInterval(timer); };
  }, [job && (job._id || job.id)]);

  // SSE layer: live swarm runs write events.ndjson immediately, while the DB
  // projector can lag by one polling tick. Use the event stream as the freshest
  // source for stage transitions, log lines, artifact unlocks, and final verdict.
  React.useEffect(() => {
    eventSeqRef.current = new Set();
    setEventState({ status: 'idle', events: [], error: null });
    setEventStages(null);
    setEventArtifacts([]);
    setEventBuild(null);
    const isLiveRun = !!(job && job.id && (job._live || job._stub || job._id));
    if (!isLiveRun) return undefined;
    // NB: do NOT gate on GooseAPI.base being truthy — in prod base='' (same-origin nginx),
    // and `'' + '/api/events/<id>'` is a valid relative URL. Gating on it left every live
    // SSE (codex + swarm) permanently unopened in prod (darkstar FS-2 #15).
    if (!(window.EventSource && window.GooseAPI)) {
      setEventState({ status: 'error', events: [], error: 'EventSource is not available in this browser.' });
      return undefined;
    }
    let closed = false;
    let es = null;
    const url = window.GooseAPI.base + '/api/events/' + encodeURIComponent(job.id);
    setEventState({ status: 'connecting', events: [], error: null });
    try {
      es = new window.EventSource(url, { withCredentials: true });
    } catch (e) {
      setEventState({ status: 'error', events: [], error: (e && e.message) || 'Could not open event stream.' });
      return undefined;
    }
    es.onopen = () => {
      if (!closed) setEventState((s) => ({ ...s, status: 'connected', error: null }));
    };
    es.onerror = () => {
      if (!closed) setEventState((s) => ({ ...s, status: 'reconnecting', error: 'Event stream reconnecting...' }));
    };
    es.onmessage = (msg) => {
      if (closed || !msg || !msg.data) return;
      let ev = null;
      try { ev = JSON.parse(msg.data); } catch (_) { return; }
      const key = ev.seq != null ? String(ev.seq) : `${ev.ts || ''}:${ev.type || ''}:${ev.stage || ''}:${ev.rel_path || ev.msg || ''}`;
      if (eventSeqRef.current.has(key)) return;
      eventSeqRef.current.add(key);
      setEventState((s) => ({
        ...s,
        status: ev.type === 'run_end' ? 'complete' : 'connected',
        error: null,
        events: [...s.events, ev].slice(-250),
      }));
      if (ev.type === 'stage_start' || ev.type === 'stage_end') {
        setEventStages((prev) => applyEventToStages(prev || job.stages, ev));
      }
      if (ev.type === 'artifact') {
        const row = eventArtifactRow(ev);
        if (row) setEventArtifacts((prev) => mergeArtifactRows(prev, [row]));
      }
      const patch = eventBuildPatch(ev);
      if (patch) {
        setEventBuild(patch);
        patchMavJob(job.id, { ...patch, _stub: false });
        closed = true;
        if (es) es.close();
      }
    };
    return () => {
      closed = true;
      if (es) es.close();
    };
  }, [job && job.id]);

  // Effective stages/artifacts: live when fetched, mock otherwise. Loading state
  // (live job with no detail yet) shows mock stages briefly, then re-renders.
  const dbStages = (detail && detail.stages.length) ? mapLiveStages(detail.stages) : null;
  let stages = collapseFigmaIngest(
    (eventStages && eventStages.length) ? eventStages : (dbStages || job.stages),
    job.source,
  );
  const mergedArtifactRows = mergeArtifactRows(detail && detail.artifacts, eventArtifacts);
  const liveArtifacts = mergedArtifactRows.length ? mapLiveArtifacts(mergedArtifactRows) : (detail ? mapLiveArtifacts(detail.artifacts) : null);
  const liveArtifactsEmpty = detail && (!mergedArtifactRows || mergedArtifactRows.length === 0);
  // Codex only emits a partial artifact-event projection (notably index.html), so its
  // canonical source-aware registry must win over that incomplete live list. Other
  // engines retain the live-first behavior; an absent engine defaults to the classic
  // swarm tree in ARTIFACTS rather than incorrectly rendering Codex paths.
  const artView = window.ARTIFACTS ? window.ARTIFACTS.forJob(job) : null;
  const useRegistry = !!(artView && (artView.engine === 'codex' || artView.engine === 'sabre'));
  const artifactGroups = useRegistry
    ? artView.groups
    : (liveArtifacts && liveArtifacts.length
      ? liveArtifacts
      : (artView && artView.groups ? artView.groups : MOCK_ARTIFACTS));

  // Effective status/verdict: prefer the live DB row over the (potentially stale)
  // MAV.jobs entry. Stub jobs surface status='queued' from the click handler;
  // dashboard rows show whatever hydrate fetched at login. Both are stale once
  // the swarm has progressed.
  // Status/verdict precedence. The DB row is authoritative once it's TERMINAL:
  // the projector preserves human review decisions (a rejected build is
  // complete/FAIL), so a replayed run_end must NOT override it back to
  // needs_review. The authoritative DB value is the per-open detail fetch once
  // it lands, else the already-hydrated MAV row (same /api/data/builds source,
  // available immediately — so we don't briefly fall back to the stale SSE
  // value while detail is in flight). The SSE eventBuild (a replayed run_end)
  // only wins while the build is genuinely LIVE (DB not yet terminal), where it
  // beats the projector's one-tick lag.
  const dbBuild = detail && detail.build;
  const dbStatus = (dbBuild && dbBuild.status) || job.status;
  const dbVerdict = (dbBuild && dbBuild.verdict) || job.verdict;
  const dbTerminal = ['complete', 'needs_review', 'failed'].includes(dbStatus);
  // A build with visible pipeline progress is RUNNING even if the DB row is a stale
  // 'queued' (the /start pre-insert before the projector's first tick, or a lagging
  // projector) — so the status pill agrees with the "N of M · running" rail instead
  // of showing "Queued" next to a half-done pipeline. Terminal DB status + a replayed
  // run_end still win.
  const stagesUnderway = stages.some((s) => s.status === 'running')
    || (stages.some((s) => s.status === 'done') && !stages.every((s) => s.status === 'done'));
  const effectiveStatus = dbTerminal ? dbStatus
    : (eventBuild && eventBuild.status) ? eventBuild.status
    : (stagesUnderway && (dbStatus === 'queued' || dbStatus === 'running')) ? 'running'
    : dbStatus;
  // Re-derive a failed run's rail from its real death point (see normalizeFailedRail)
  // so a Semantic-stage crash doesn't render as "Semantic running · Execution failed".
  if (effectiveStatus === 'failed') stages = normalizeFailedRail(stages);
  const effectiveVerdict = dbTerminal
    ? (dbVerdict || (eventBuild && eventBuild.verdict))
    : ((eventBuild && eventBuild.verdict) || dbVerdict);
  // Running = the swarm is actively executing. Use stages first: if every stage
  // is 'done' the build is finished regardless of what the stale row says.
  const allStagesDone = stages.length > 0 && stages.every((s) => s.status === 'done');
  const anyStageRunning = stages.some((s) => s.status === 'running');
  // A terminal build (failed/complete/needs_review) is never "running", even if a
  // stale rail still has a stage marked running.
  const terminalStatus = ['failed', 'complete', 'needs_review'].includes(effectiveStatus);
  const running = !allStagesDone && !terminalStatus && (anyStageRunning || effectiveStatus === 'running');
  // Codex usage-limit pop-out: a codex build runs on a ChatGPT-subscription quota
  // that can cap mid-build. On a 429 usage_limit the supervisor PAUSES the build
  // (paused, NOT failed — verdict stays out of FAIL) and surfaces job.limitHit +
  // job.limitResetAt (ISO, the quota-reset header). Show the exact reset time + a
  // live countdown; it re-renders each second via the running `tick` below. No
  // limitResetAt → a demo offset so the offline mock preview still ticks.
  const codexLimit = running && job.engine === 'codex' && job.limitHit && !limitDismissed;
  const resetAt = React.useMemo(
    () => (job.limitResetAt ? new Date(job.limitResetAt) : new Date(Date.now() + (2 * 3600 + 14 * 60 + 8) * 1000)),
    [job.id, job.limitResetAt]
  );
  const resetClock = resetAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const resetTz = (resetAt.toLocaleTimeString([], { timeZoneName: 'short' }).split(' ').pop()) || '';
  const resetMs = Math.max(0, resetAt - Date.now());
  const resetH = Math.floor(resetMs / 3600000), resetM = Math.floor((resetMs % 3600000) / 60000);
  // Which stage is currently executing (or the first not-done one if "running"
  // status hasn't propagated yet). Drives the "Stage NN · Name" panel header.
  const activeStageIdx = anyStageRunning
    ? stages.findIndex((s) => s.status === 'running')
    : stages.findIndex((s) => s.status !== 'done');
  const activeStage = activeStageIdx >= 0 ? stages[activeStageIdx] : null;
  // PY-10 / Pylon: the afterburner (cross-client visual QA on the FINAL index.html) is engine-agnostic.
  // The old gate required the swarm 6-stage QA slot (stages[4]) to be 'done' — codex/claude agentic builds
  // never fill that slot, so the button greyed out forever ("QA stage not completed") even at needs_review.
  // Gate on a FINISHED build with reviewable output (any engine); the backend (eoa-visual-qa) still
  // validates the HTML exists before kicking off.
  const afterEligible = effectiveStatus === 'complete' || effectiveStatus === 'needs_review';

  // Roll-up tokens + cost for the header cards. Prefer LIVE stages summed
  // client-side (instant when a build is mid-run); fall back to the build
  // row's tokens_k/cost (set by projector roll-up after the last stage)
  // and finally to the mock job's pre-baked values. Tokens shown in K to
  // match the prototype's display.
  const totalsLabel = (() => {
    if (detail && detail.stages && detail.stages.length) {
      let tokens = 0, cost = 0;
      for (const s of detail.stages) {
        if (s.tokens != null) tokens += Number(s.tokens) || 0;
        if (s.cost != null)   cost   += Number(s.cost) || 0;
      }
      return {
        tokens: tokens > 0 ? (tokens >= 1000 ? (tokens / 1000).toFixed(1) + 'K' : tokens + '') : '—',
        cost:   cost > 0 ? '$' + cost.toFixed(2) : '$0.00',
      };
    }
    return {
      tokens: (job.tokensK != null && job.tokensK !== 0) ? Number(job.tokensK) + 'K' : '0K',
      cost:   '$' + Number(job.cost || 0).toFixed(2),
    };
  })();
  const loadAbStatus = async () => {
    if (!(job && job.id && window.GooseAPI && window.GooseAPI.afterburnerStatus)) return null;
    try {
      const next = await window.GooseAPI.afterburnerStatus(job.id);
      setAbStatus(next);
      return next;
    } catch (e) {
      setAbStatus({ jobId: job.id, status: 'error', phase: 'error', progress: 100, error: (e && e.message) || 'Afterburner status failed.' });
      return null;
    }
  };
  // Operator kill: force a running/stuck build to terminal 'failed' (the backend
  // also stops the swarm proc + records meta.cancelled so the projector won't
  // revert it). Optimistically flip the local status so the pill/rail/list update
  // without waiting for a re-hydrate.
  const cancelBuild = async () => {
    if (!(job && job.id && window.GooseAPI && window.GooseAPI.cancelBuild)) return;
    if (typeof window !== 'undefined' && window.confirm
      && !window.confirm('Kill this build? It will be marked failed and the swarm stopped.')) return;
    setCancelling(true);
    try {
      const r = await window.GooseAPI.cancelBuild(job.id);
      const st = (r && r.build && r.build.status) || 'failed';
      setEventBuild({ status: st });
      patchMavJob(job.id, { status: st, _stub: false });
    } catch (e) {
      alert((e && e.message) || 'Could not cancel the build.');
    } finally { setCancelling(false); }
  };

  // Open only an authoritative physical row. Never spread the parent's verdict,
  // approval, progress or assets into a synthetic child, and never restart it.
  const openAttempt = async (jobId, view) => {
    const context = window.MAV.__contextKey, epoch = window.MAV.__contextEpoch;
    const known = attemptHistory?.attempts?.find(a => a.job_id === jobId);
    const direct = known?.build_id ? await window.GooseAPI.build(known.build_id) : null;
    const list = direct?.build ? { builds: [direct.build] } : await window.GooseAPI.builds();
    if (view !== retryView.current || window.MAV.__contextKey !== context || window.MAV.__contextEpoch !== epoch) return;
    const row = (list?.builds || []).find(b => b.job_id === jobId);
    if (!row) throw Error('The retained attempt is not in the current build list. Check its status again; do not submit another retry.');
    const child = mapBuild(row);
    window.MAV.jobs = [child, ...window.MAV.jobs.filter(j => j.id !== child.id)];
    if (typeof openJob === 'function') openJob(child.id);
  };
  const retryBuild = async (receiptKey = null) => {
    if (!canRetry || resuming || !window.GooseAPI?.retryBuild) return;
    const view = retryView.current;
    setResumeError(null); setResuming(true);
    try {
      const r = receiptKey ? await window.GooseAPI.checkRetryRequest(job.id, receiptKey)
        : await window.GooseAPI.retryBuild(job.id, detail?.build?.meta?.dispatch?.attempt_id);
      if (view !== retryView.current) return;
      refreshRetryReceipts();
      if (r.pending || !r.job_id) {
        setResumeError('Retry awaiting confirmation (' + (r.dispatch_status || 'unknown') + '). Check status using the saved receipt; this does not run another model.');
      } else await openAttempt(r.job_id, view);
    } catch (e) {
      if (view === retryView.current) { refreshRetryReceipts(); setResumeError(['network','timeout'].includes(e?.kind)
        ? 'Retry response was not confirmed. Check the saved receipt before submitting again; the server may already have reserved the new attempt.'
        : (e && e.message) || 'Could not confirm retry. Check the saved receipt before trying again.'); }
    } finally { if (view === retryView.current) setResuming(false); }
  };

  const reconcileWorker = async () => {
    if (!canRetry || resuming || !detail?.build?.meta?.dispatch?.attempt_id) return;
    const view=retryView.current,context=window.MAV.__contextKey,epoch=window.MAV.__contextEpoch;
    const current=()=>view===retryView.current && context===window.MAV.__contextKey && epoch===window.MAV.__contextEpoch;
    setResuming(true);setResumeError(null);setRecoveryNotice(null);
    try {
      const result=await window.GooseAPI.reconcileBuild(job.id,detail.build.meta.dispatch.attempt_id);
      if (!current()) return;
      const fresh=await window.GooseAPI.build(detail.build.id);
      if (!current()) return;
      if (fresh?.build && Array.isArray(fresh.stages)) {
        setDetail(fresh);patchMavJob(job.id,{status:fresh.build.status,verdict:fresh.build.verdict || null});
      }
      setRecoveryNotice(result.outcome==='interrupted'
        ? 'The worker is safely closed, but this run did not publish a complete result. Its files are retained. Review them before explicitly retrying as a new attempt.'
        : 'The worker is safely closed and its retained result has been reconciled. Existing QA and independent approval gates still apply; reconciliation does not approve delivery.');
    } catch(e) {if(current())setResumeError(e.message || 'Worker recovery could not be confirmed. No replacement was launched.');}
    finally {if(current())setResuming(false);}
  };

  const runAb = async (force = false, intake = { method: 'api', url: null }) => {
    if (!(job && job.id && window.GooseAPI && window.GooseAPI.startAfterburner)) return;
    setAbIntake(false);
    setAbBusy(true);
    try {
      const next = await window.GooseAPI.startAfterburner(job.id, force, intake);
      setAbStatus(next);
    } catch (e) {
      setAbStatus({ jobId: job.id, status: 'error', phase: 'error', progress: 100, error: (e && e.message) || 'Afterburner start failed.' });
    } finally {
      setAbBusy(false);
    }
  };
  React.useEffect(() => {
    setAbStatus(null);
    setAbBusy(false);
    if (job && job._id) loadAbStatus();
  }, [job.id]);
  React.useEffect(() => {
    if (!abStatus || !['queued', 'running'].includes(abStatus.status)) return undefined;
    const t = setInterval(loadAbStatus, 2500);
    return () => clearInterval(t);
  }, [job.id, abStatus && abStatus.status]);
  React.useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const lastDone = stages.reduce((acc, s, i) => s.status === 'done' ? i : acc, -1);
  const failedStage = effectiveStatus === 'failed' ? stages.findIndex(s => s.status === 'failed') : -1;
  // Genuine pipeline-failure details, derived from REAL data (never hardcoded):
  // the failure point is the stage the swarm marked failed, else the first
  // not-done stage (where it got stuck). failureError is the actual error text
  // the swarm reported on run_end or in an error-level log line, if any.
  const failurePointIdx = effectiveStatus === 'failed'
    ? (failedStage >= 0 ? failedStage : stages.findIndex(s => s.status !== 'done'))
    : -1;
  const failurePoint = failurePointIdx >= 0 ? stages[failurePointIdx] : null;
  const failureError = (() => {
    if (effectiveStatus !== 'failed') return null;
    const evs = (eventState && eventState.events) || [];
    for (let i = evs.length - 1; i >= 0; i--) {
      const e = evs[i];
      if (!e) continue;
      if (e.type === 'run_end' && e.error) return String(e.error);
      if (e.type === 'log' && /error|failed|exception|traceback|timed?\s*out|timeout|429|rate.?limit/i.test(e.msg || '')) return String(e.msg);
    }
    return null;
  })();
  const abRunning = abBusy || ['queued', 'running'].includes(abStatus?.status);
  const abStarted = abStatus && abStatus.status && abStatus.status !== 'not_started';
  const abForce = ['complete', 'skipped', 'timeout', 'error'].includes(abStatus?.status);

  return (
    <div className="page fade-in">
      {/* Header */}
      <div className="page-head" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="row gap-10" style={{ marginBottom: 8 }}>
            <button className="btn btn-quiet" style={{ padding: 6 }} onClick={back || (() => go('builds'))}><I.chevron size={16} style={{ transform: 'rotate(180deg)' }} /></button>
            <ClientBadge slug={clientSlug} size={30} />
            <StatusPill status={effectiveStatus} verdict={effectiveVerdict} />
            <span className="chip">{(job.source || 'pdf').toUpperCase()}</span>
            {(job.confidence != null && job.confidence < 0.75) && <span className="chip pill-cond" style={{ fontWeight: 600 }}>Client confidence {Math.round(job.confidence * 100)}%</span>}
          </div>
          <h1 className="page-title" style={{ fontSize: 23 }}>{job.name}</h1>
          <div className="page-desc mono" style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <span>{job.id}</span>
            <span>·</span>
            {canReassign
              ? <BuildClientPicker jobId={job.id} value={clientSlug} onAssigned={handleClientAssigned} />
              : <span className="client-assign" title="Reassigning the client is for team leads, producers, and admins">{(window.MAV.clientBySlug(clientSlug) || {}).name || clientSlug || 'Unassigned'}</span>}
            <span>· started {job.started}</span>
          </div>
          {(() => {
            const { creditMember, MiniAvatar, shortName } = window;
            if (!creditMember) return null;
            const builder = creditMember(job.builderId || job.owner);
            const approver = job.approvedById ? creditMember(job.approvedById) : null;
            let pending = 'Awaiting review';
            if (job.status === 'running' || job.status === 'queued') pending = 'In build';
            else if (job.status === 'failed') pending = 'Not greenlit';
            return (
              <div className="row gap-8 wrap" style={{ marginTop: 10 }}>
                {builder && (
                  <span className="credit-chip" title={'Built by ' + builder.name}>
                    <I.builds size={12} className="credit-ico" /><MiniAvatar m={builder} size={16} />
                    <span className="cc-lbl">Built</span><b>{shortName(builder.name)}</b>
                  </span>
                )}
                <span className="credit-chip" title={approver ? 'Greenlit by ' + approver.name : pending}>
                  <I.check size={12} className={'credit-ico' + (approver ? ' ok' : '')} />
                  {approver
                    ? <React.Fragment><MiniAvatar m={approver} size={16} /><span className="cc-lbl">Greenlit</span><b>{shortName(approver.name)}</b></React.Fragment>
                    : <span className="credit-pending">{pending}</span>}
                </span>
              </div>
            );
          })()}
        </div>
        <div className="card card-pad" style={{ padding: '12px 16px', flexShrink: 0 }} title="Fuel burned this sortie.">
          <div className="row gap-16">
            <div><div className="muted" style={{ fontSize: 10.5, fontWeight: 600 }}>TOKENS</div><div className="mono" style={{ fontSize: 16, fontWeight: 700 }}>{totalsLabel.tokens}</div></div>
            <div style={{ width: 1, background: 'var(--hairline)', alignSelf: 'stretch' }}></div>
            <div><div className="muted" style={{ fontSize: 10.5, fontWeight: 600 }}>COST</div><div className="mono" style={{ fontSize: 16, fontWeight: 700 }}>{totalsLabel.cost}</div></div>
          </div>
        </div>
      </div>

      {/* Action bar — a full-width wrapping row BELOW the header. These buttons used to sit
          in page-head's right column under the stats card; at mid-widths the flex header
          wrapped and that whole stats+buttons column floated detached in the center. A
          full-width row is the responsive home: the header keeps [title/meta | stats]; the
          buttons flow + wrap on their own, with the destructive Delete pushed isolated to
          the right via the grow spacer. */}
      <div className="row gap-8 wrap" style={{ marginBottom: 20 }}>
        {!terminalStatus && canCancel && <button className="btn btn-ghost" onClick={cancelBuild} disabled={cancelling} title="Force-stop this build (marks it failed + kills the swarm)"><I.cancel size={15} /> {cancelling ? 'Cancelling…' : 'Cancel'}</button>}
        {effectiveStatus === 'failed' && canRetry && <button className="btn btn-ghost" data-action="retry-build" onClick={() => retryBuild()} disabled={resuming || !detail?.build?.meta?.dispatch?.attempt_id} title="Create one new attempt from retained original inputs. This failed build and its history remain unchanged."><I.retry size={15} /> {resuming ? 'Checking retry…' : 'Retry as new attempt'}</button>}
        {canRetry && detail?.build?.meta?.dispatch?.attempt_id && ['queued','running','failed'].includes(effectiveStatus) &&
          <button className="btn btn-quiet" data-action="reconcile-worker" disabled={resuming} onClick={reconcileWorker} title="Read the protected worker receipt and reconcile this execution only. Never launches a model or a replacement.">Reconcile worker status</button>}
        {effectiveStatus === 'needs_review' && <button className="btn btn-primary" onClick={openReview}><I.review size={15} /> Open review</button>}
        {effectiveStatus === 'complete' && <button className="btn btn-primary" onClick={downloadDelivery}><I.download size={15} /> Download zip</button>}
        {effectiveStatus === 'complete' && <button className="btn btn-ghost" onClick={openReview} data-action="reopen-review"><I.review size={15} /> Reopen review</button>}
        {(effectiveStatus === 'complete' || effectiveStatus === 'needs_review') &&
          <button className="btn btn-ghost" onClick={() => setReporting(true)} title="Report a problem found on this shipped build"><I.flag size={15} /> Report an issue</button>}
        {afterEligible
          ? <button className="btn btn-after" onClick={() => setAbIntake(true)} disabled={abRunning} title="Cross-client visual-diff QA via Email on Acid">
              {abRunning ? <React.Fragment><span className="spinner"></span> Running Afterburner…</React.Fragment>
                : abForce ? <React.Fragment><I.flame size={16} fill="currentColor" /> Re-run Afterburner QA</React.Fragment>
                : <React.Fragment><I.flame size={16} fill="currentColor" /> Afterburner QA</React.Fragment>}
            </button>
          : <button className="btn btn-ghost" disabled title="Available once the build finishes (review / complete)"><I.flame size={16} fill="currentColor" /> Afterburner QA</button>}
        <span className="grow"></span>
        {canDelete && <button className="btn btn-danger-ghost" onClick={() => setDeleting(true)} title="Permanently delete this build (admin / producer)"><I.trash size={15} /> Delete build</button>}
        {resumeError && <span className="muted" role="alert" data-resume-error style={{ fontSize: 12, color: 'var(--bad, #c0392b)', width: '100%' }}>{resumeError}</span>}
        {dlErr && <span className="muted" role="alert" style={{ fontSize: 12, color: 'var(--bad, #c0392b)', width: '100%' }}>{dlErr}</span>}
      </div>

      {retryReceipts.length > 0 && <div className="card card-pad mb-20" data-retry-receipts>
        <div className="sec-title">Retained retry requests</div>
        <p className="muted">Checking these receipts never launches a model. The failed build keeps its files and history.</p>
        {retryReceipts.map(r => <div key={r.key} className="row gap-12 wrap mt-10">
          <span className="grow">{r.job_id || 'Awaiting attempt identity'} · {r.dispatch_status || 'unconfirmed'}</span>
          <button className="btn btn-quiet" disabled={resuming || !canRetry} onClick={() => retryBuild(r.key)}>Check retry status</button>
          {r.job_id && <button className="btn btn-quiet" disabled={resuming} onClick={() => openAttempt(r.job_id,retryView.current).catch(e=>setAttemptError(e.message))}>Open reserved attempt</button>}
        </div>)}
      </div>}
      {recoveryNotice && <p role="status" data-recovery-notice>{recoveryNotice}</p>}
      {attemptHistory?.attempts?.length > 1 && <div className="card card-pad mb-20" data-build-attempts>
        <div className="sec-title">Build attempts — separate files and review history</div>
        {attemptHistory.attempts.map(a => <div key={a.job_id} className="row gap-12 wrap mt-10">
          <span className="grow">Attempt {a.ordinal || '—'} · {a.job_id} · {a.status}{a.job_id === attemptHistory.current_job_id ? ' · latest' : ''}</span>
          <button className="btn btn-quiet" disabled={a.job_id === job.id} onClick={() => openAttempt(a.job_id, retryView.current).catch(e => setAttemptError(e.message))}>Open attempt</button>
        </div>)}
        {attemptHistory.truncated && <p className="muted">Showing the latest 100 retained attempts.</p>}
      </div>}
      {attemptError && <p className="muted" role="alert">Attempt history unavailable: {attemptError}</p>}

      {/* Send to Dropbox — deliver a packaged build to the client's Dropbox (Viper
          Goose-14). Gated on a packaged status; the card self-degrades honestly
          until the DBX-01 route lands (no FE change when it does). */}
      {(effectiveStatus === 'complete' || effectiveStatus === 'needs_review') && window.SendToDropbox &&
        <div style={{ marginBottom: 20 }}><SendToDropbox job={job} /></div>}

      {/* Codex usage-limit — build paused until the quota window resets. Shows only
          for a running codex build flagged job.limitHit (a 429 usage_limit pause). */}
      {codexLimit && (
        <div className="codex-limit">
          <span className="cl-ico"><I.warn size={20} /></span>
          <div className="grow">
            <div className="cl-title">Codex usage limit reached — build paused</div>
            <div className="cl-msg">Your ChatGPT-subscription Codex quota is exhausted. This build holds at its current step and <b>auto-resumes</b> the moment the window resets — no work is lost.</div>
            <div className="cl-reset">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
              Resets at <b>{resetClock} {resetTz}</b><span className="cl-sep">·</span>in {resetH}h {String(resetM).padStart(2, '0')}m
            </div>
          </div>
          <div className="cl-acts">
            <button className="btn btn-quiet" style={{ padding: '7px 13px', fontSize: 12.5 }} onClick={() => setLimitDismissed(true)}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Banners — driven by the real status/verdict + real failure data, never
          hardcoded copy. (A QA verdict of FAIL is a completed build awaiting
          review, not a pipeline crash — see eventBuildPatch / db projector.) */}
      {effectiveStatus === 'failed' && (
        <div className="blocker" style={{ marginBottom: 20 }}>
          <span className="bi"><I.x size={20} /></span>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--fail)' }}>
              Pipeline failed{failurePoint ? ` at ${failurePoint.name} (stage ${failurePoint.no})` : ''}
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              {failureError || 'A stage failed before the run finished. This attempt and its files are retained. A qualified retry creates a separate attempt from the original inputs.'}
            </div>
          </div>
        </div>
      )}
      {effectiveStatus === 'needs_review' && (() => {
        const v = String(effectiveVerdict || '').toUpperCase();
        const isFail = v === 'FAIL';
        const accent = isFail ? 'var(--fail)' : 'var(--cond)';
        const title = isFail ? 'FAIL — automated QA flagged blocking issues'
          : v === 'CONDITIONAL' ? 'CONDITIONAL — one or more items need confirmation'
          : 'Needs your review';
        const body = isFail
          ? 'The QA gate scored this completed build FAIL. Open the review to see the findings, then re-run from Execution or reject.'
          : v === 'CONDITIONAL'
            ? "Good build — a few flagged items want a reviewer's eyes before it ships."
            : 'This completed build is waiting on a human decision.';
        return (
          <div className={'verdict-banner ' + (isFail ? 'fail' : 'cond')} style={{ marginBottom: 20 }}>
            <span className="vb-ico" style={{ color: accent }}><I.warn size={22} /></span>
            <div className="grow">
              <div style={{ fontSize: 15, fontWeight: 800, color: accent }}>{title}</div>
              <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{body}</div>
            </div>
            <button className="btn btn-primary" onClick={openReview}>Review now <I.arrow size={15} /></button>
          </div>
        );
      })()}

      {/* Pipeline */}
      <div
        className="card card-pad"
        style={{ marginBottom: 20 }}
        data-stage-source={(eventStages && eventStages.length) ? 'sse' : (dbStages ? 'db' : 'job')}
        data-event-count={eventState.events.length}
      >
        {(() => {
          // Engine-aware Process card (feature 2): codex builds run an agentic
          // loop, not the deterministic 6-stage rail — render the
          // build→render→critique→fix stepper + metrics instead. Swarm/router/
          // legacy keep the PipelineStepper + StageLogAccordion, unchanged.
          const isCodex = !!(window.CodexRunPanel && job.engine === 'codex');
          return (
            <React.Fragment>
              <div className="sec-head"><span className="sec-title">{isCodex ? 'Process' : 'Pipeline'}</span>
                <span className="muted" style={{ fontSize: 12 }}>{isCodex
                  ? (running ? 'codex agentic stream' : 'agentic build')
                  : `${lastDone + 1} of ${stages.length} stages complete${running ? ' · running' : ''}`}</span>
              </div>
              {isCodex ? (
                <CodexRunPanel job={job} running={running} events={eventState.events} />
              ) : (
                <React.Fragment>
                  <PipelineStepper stages={stages} />
                  {(job._id || job._stub || job._live) && stages && stages.length > 0 && (
                    <StageLogAccordion stages={stages} events={eventState.events} />
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          );
        })()}
      </div>

      <div className="grid-hero">
        {/* Active stage / preview */}
        <div className="card card-pad">
          {running ? (
            <React.Fragment>
              <div className="sec-head">
                <span className="sec-title"><span className="pill pill-run" style={{ marginRight: 8 }}><span className="dot"></span></span>{job.engine === 'codex' ? 'Live activity' : (activeStage ? `Stage ${activeStage.no} · ${activeStage.name}` : 'Starting…')}</span>
                <span className="chip chip-mono">{job.engine === 'codex' ? 'codex · agentic stream' : (activeStage ? `${activeStage.model || '—'}${activeStage.elapsed && activeStage.elapsed !== '—' ? ' · ' + activeStage.elapsed : ''}` : 'queued')}</span>
              </div>
              {/* Prototype-only jobs keep the looping demo log (engine-aware:
                  codex → CODEX_LOG, swarm → MAV.liveLog). Live runs render the SSE
                  replay/tail feed from events.ndjson (the real codex stream tap
                  feeds here too — supervisor / PY-05). */}
              {!(job._live || job._stub || job._id) ? (
                <LiveLog job={job} />
              ) : job.engine === 'codex' ? (
                <CodexStreamLog events={eventState.events} status={eventState.status} />
              ) : (
                <ActiveStageLog events={eventState.events} status={eventState.status} error={eventState.error} stageId={activeStage && activeStage.id} />
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <div className="sec-head">
                <span className="sec-title">Rendered email</span>
                <div className="tabs">
                  <span className={'tab' + (device === 'desktop' ? ' active' : '')} onClick={() => setDevice('desktop')}>Desktop 600px</span>
                  <span className={'tab' + (device === 'mobile' ? ' active' : '')} onClick={() => setDevice('mobile')}>Mobile 375px</span>
                </div>
              </div>
              {/* Real screenshot when this is a live job (DB uuid present and
                  hydrate stamped a job_id slug into job.id). Mock jobs keep
                  the prototype placeholder so the demo flow without a server
                  still renders. */}
              {job._id && true /* live discriminator is job._id alone */ ? (
                <RealEmailPreview jobId={job.id} device={device} />
              ) : (
                <div className="preview-frame">
                  <div className="email-mock" style={{ width: device === 'mobile' ? 300 : 440, transition: 'width .25s ease' }}>
                    <div style={{ background: c.accent, height: 8 }}></div>
                    <div style={{ padding: '18px 20px' }}>
                      <div style={{ height: 16, width: 90, background: c.color, borderRadius: 2 }}></div>
                      <div style={{ height: 110, background: 'repeating-linear-gradient(135deg,#eee,#eee 8px,#f6f6f6 8px,#f6f6f6 16px)', borderRadius: 3, margin: '14px 0', display: 'grid', placeItems: 'center', color: '#aaa', fontFamily: 'var(--mono)', fontSize: 11 }}>hero.gif</div>
                      <div style={{ height: 11, background: '#2c2c2c', borderRadius: 2, width: '85%' }}></div>
                      <div style={{ height: 8, background: '#ddd', borderRadius: 2, width: '100%', marginTop: 9 }}></div>
                      <div style={{ height: 8, background: '#ddd', borderRadius: 2, width: '70%', marginTop: 6 }}></div>
                      <div style={{ height: 70, background: 'repeating-linear-gradient(135deg,#eee,#eee 8px,#f6f6f6 8px,#f6f6f6 16px)', borderRadius: 3, margin: '14px 0', display: 'grid', placeItems: 'center', color: '#aaa', fontFamily: 'var(--mono)', fontSize: 11 }}>rate_table.png</div>
                      <div style={{ height: 34, background: c.accent, borderRadius: 3, display: 'grid', placeItems: 'center', color: c.color === '#3B3C43' ? '#1B1B1B' : '#fff', fontSize: 11, fontWeight: 700 }}>CTA composite</div>
                      <div style={{ height: 6, background: '#e8e8e8', borderRadius: 2, width: '60%', marginTop: 16 }}></div>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          )}
        </div>

        {/* Artifacts rail */}
        <div className="card card-pad" data-artifacts-source={useRegistry ? 'registry' : (liveArtifacts && liveArtifacts.length ? 'live' : (liveArtifactsEmpty ? 'live-empty' : 'mock'))}>
          <div className="sec-head"><span className="sec-title">Artifacts</span><span className="muted" style={{ fontSize: 11 }}>{artView ? artView.label + ' · build outputs' : 'per-stage outputs'}</span></div>
          {!useRegistry && liveArtifactsEmpty && (
            <div className="muted" style={{ fontSize: 12, padding: '8px 4px 12px', lineHeight: 1.45 }}>
              No artifacts projected for this build. Showing the canonical paths the swarm always writes — click any row to fetch it; missing files surface as a 404 in the viewer.
            </div>
          )}
          {artifactGroups.map((a, i) => {
            // engine-aware groups (artifacts.js) carry a pre-computed `state`; the
            // swarm groups carry a numeric `stage` + lastDone/failedStage — support both.
            const isLocked = a.state ? a.state === 'locked' : (a.stage > lastDone && a.stage !== failedStage);
            const isFail = a.state ? a.state === 'failed' : (a.stage === failedStage);
            const open = openArt === i;
            const Ico = ICO(a.ico);
            return (
              <div key={i} className={'artifact-group' + (a.kind === 'inputs' ? ' is-inputs' : '')}>
                <button className={'artifact' + (isLocked ? ' locked' : '') + (isFail ? ' failed' : '')} onClick={() => !isLocked && setOpenArt(open ? null : i)} disabled={isLocked}>
                  <span className="ai"><Ico size={17} /></span>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="an" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.n}</div>
                    <div className="ad">{isLocked ? 'Locked' : isFail ? 'Failed · partial output + raw response' : a.d}</div>
                  </div>
                  {a.kind === 'inputs' && <span className="art-count">{a.files.length} files</span>}
                  {isFail && <span className="pill pill-fail" style={{ flexShrink: 0, fontSize: 9.5 }}><I.warn size={10} />partial</span>}
                  {!isLocked && <I.chevron size={15} className={'art-chev' + (open ? ' open' : '')} style={{ color: 'var(--faint)' }} />}
                </button>
                {open && !isLocked && (
                  <div className="artifact-files">
                    {a.files.map((f, j) => {
                      const diag = isFail && f.raw;
                      const partial = isFail && f.primary;
                      const rel = a.dir + f.n;
                      // Click opens the viewer modal for live builds with a real
                      // job_id slug; mock files just show their description.
                      const canFetch = !!(job._id && true /* live discriminator is job._id alone */);
                      const onClick = (e) => {
                        e.stopPropagation();
                        if (canFetch) setViewerArtifact({ relPath: rel });
                      };
                      return (
                        <button
                          type="button"
                          key={j}
                          className={'afile' + (diag ? ' diag' : '') + (partial ? ' partial' : '')}
                          onClick={onClick}
                          disabled={!canFetch}
                          data-afile-rel={rel}
                          style={{ background: 'none', border: 0, padding: 0, textAlign: 'left', width: '100%', cursor: canFetch ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 8 }}
                          title={canFetch ? 'Open this file' : 'Available once the run dir is present on disk'}
                        >
                          <span className="afile-dot"></span>
                          <span className="afile-n mono">{rel}</span>
                          <span className="afile-d">{diag ? 'finish_reason: length · truncated mid-output' : partial ? 'partial — cut off, not packaged' : f.d}</span>
                          {f.raw && <span className="afile-tag">raw</span>}
                        </button>
                      );
                    })}
                    {isFail && <div className="afile-hint"><I.warn size={11} /> Inspect the partial artifact and <b>raw-response.json</b>. Any retry is a new attempt; this evidence remains unchanged.</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {abStarted && <div className="mt-20"><AfterburnerQA status={abStatus} job={displayJob} onRefresh={loadAbStatus} /></div>}
      {abIntake && (
        <AfterburnerIntake
          job={displayJob}
          onClose={() => setAbIntake(false)}
          onRun={(intake) => runAb(abForce, intake)}
        />
      )}
      {reporting && <ReportIssue job={displayJob} onClose={() => setReporting(false)} onOpenIssue={(id) => { window.__pendingIssue = id; go('admin'); }} />}
      {deleting && <DeleteBuildModal job={displayJob} onClose={() => setDeleting(false)} onDeleted={async () => { await window.GooseAPI.deleteBuild(displayJob.id); go('builds'); }} />}
      {viewerArtifact && <ArtifactViewer jobId={job.id} relPath={viewerArtifact.relPath} onClose={() => setViewerArtifact(null)} />}
    </div>
  );
}
Object.assign(window, { JobDetail, PipelineStepper, LiveLog, LiveEventLog, StageStreamLog, ActiveStageLog, StageLogAccordion });
